import React, { useEffect, useState } from "react";
import { fetchServiceBookings, updateServiceBooking, noShowBooking, completeBooking, fetchServicesSummary, fetchServiceBookingReceipt, getCurrency, getRole } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Services & Memberships — bookings board (SM3). A log of recent service
// bookings per area; the desk can tag a therapist/room and change status.
const S = {
  en: {
    title: "Bookings", subtitle: "Recent spa / gym / pool bookings and sales",
    all: "All", spa: "Spa", gym: "Gym", pool: "Pool", salon: "Salon", activity: "Activities",
    none: "No bookings yet.", resource: "Therapist / room", save: "Save",
    staff: "Staff", room: "Room", print: "Print", receipt: "Receipt",
    r_subtotal: "Subtotal", r_service: "Service", r_vat: "VAT", r_total: "Total",
    k_revenue: "Services revenue", k_dues: "Membership dues", k_members: "Active members", k_bookings: "Bookings",
    b_folio: "Room bill", b_paid: "Paid", b_membership: "Member", b_paid_pending: "To pay",
    st_booked: "Booked", st_done: "Done", st_cancelled: "Cancelled", st_no_show: "No-show",
    noshow_charge_q: "Charge the no-show fee of {amt}? (Cancel = mark no-show without charging.)",
    cancel: "Cancel", reschedule: "Reschedule", cancel_q: "Cancel this booking?",
    confirm: "Confirm", new_time: "New date & time", conflict: "That slot clashes with another booking.",
    err: "Could not update.", options: "Options", special_request: "Special request",
    collect: "Collect payment", method: "Payment", m_cash: "Cash", m_telebirr: "telebirr", m_cbe: "CBE Birr", m_pos: "POS",
  },
  am: {
    title: "ማስያዣዎች", subtitle: "የቅርብ ጊዜ ስፓ / ጂም / መዋኛ ማስያዣና ሽያጭ",
    all: "ሁሉም", spa: "ስፓ", gym: "ጂም", pool: "መዋኛ", salon: "ሳሎን", activity: "እንቅስቃሴዎች",
    none: "እስካሁን ማስያዣ የለም።", resource: "ቴራፒስት / ክፍል", save: "አስቀምጥ",
    staff: "ሰራተኛ", room: "ክፍል", print: "አትም", receipt: "ደረሰኝ",
    r_subtotal: "ንዑስ ድምር", r_service: "አገልግሎት", r_vat: "ተ.እ.ታ", r_total: "ጠቅላላ",
    k_revenue: "የአገልግሎት ገቢ", k_dues: "የአባልነት ክፍያ", k_members: "ገቢር አባላት", k_bookings: "ማስያዣዎች",
    b_folio: "የክፍል ሂሳብ", b_paid: "ተከፍሏል", b_membership: "አባል", b_paid_pending: "ይከፈላል",
    st_booked: "ተይዟል", st_done: "ተጠናቋል", st_cancelled: "ተሰርዟል", st_no_show: "አልመጣም",
    noshow_charge_q: "የ{amt} አልመጣም ክፍያ ይከፈል? (ይቅር = ሳይከፈል አልመጣም ምልክት)",
    cancel: "ሰርዝ", reschedule: "እንደገና ያዙ", cancel_q: "ይህን ማስያዣ ይሰረዝ?", // TODO verify am
    confirm: "አረጋግጥ", new_time: "አዲስ ቀንና ሰዓት", conflict: "ይህ ሰዓት ከሌላ ማስያዣ ጋር ይጋጫል።", // TODO verify am
    err: "ማዘመን አልተሳካም።", options: "አማራጮች", special_request: "ልዩ ጥያቄ", // TODO verify am
    collect: "ክፍያ ሰብስብ", method: "ክፍያ", m_cash: "ጥሬ ገንዘብ", m_telebirr: "ቴሌብር", m_cbe: "ሲቢኢ ብር", m_pos: "ፖስ",
  },
  om: {
    title: "Qabannoowwan", subtitle: "Qabannoo fi gurgurtaa spaa / jiimii / daakaa dhihoo",
    all: "Hunda", spa: "Spaa", gym: "Jiimii", pool: "Daakaa", salon: "Saaloonii", activity: "Sochiiwwan",
    none: "Hanga ammaa qabannoon hin jiru.", resource: "Terapistii / kutaa", save: "Olkaa'i",
    staff: "Hojjetaa", room: "Kutaa", print: "Maxxansi", receipt: "Nagahee",
    r_subtotal: "Ida'ama xiqqaa", r_service: "Tajaajila", r_vat: "VAT", r_total: "Ida'ama",
    k_revenue: "Galii tajaajilaa", k_dues: "Kaffaltii miseensaa", k_members: "Miseensota hojjetan", k_bookings: "Qabannoowwan",
    b_folio: "Herrega kutaa", b_paid: "Kaffalame", b_membership: "Miseensa", b_paid_pending: "Ni kaffalama",
    st_booked: "Qabame", st_done: "Xumurame", st_cancelled: "Haqame", st_no_show: "Hin dhufne",
    noshow_charge_q: "Kaffaltii hin dhufne {amt} kaffalchiisi? (Haqi = malee mallatteessi)",
    cancel: "Haqi", reschedule: "Irra deebi'ii qabi", cancel_q: "Qabannoo kana haquu?", // TODO verify om
    confirm: "Mirkaneessi", new_time: "Guyyaa fi sa'aatii haaraa", conflict: "Sa'aatiin kun beellama biraa waliin wal dhaba.", // TODO verify om
    err: "Haaromsuun hin dandeenye.", options: "Filannoowwan", special_request: "Gaaffii addaa", // TODO verify om
    collect: "Kaffaltii sassaabi", method: "Kaffaltii", m_cash: "Maallaqa", m_telebirr: "telebirr", m_cbe: "CBE Birr", m_pos: "POS",
  },
};

const AREAS = ["spa", "gym", "pool", "salon", "activity"];
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: "7px 9px", border: "1px solid #d8dee2", borderRadius: 8, fontSize: 13 };
const AREA_TINT = { spa: "#EAF3F0", gym: "#EEF2FB", pool: "#EAF6FC", salon: "#FBEFF4", activity: "#FBF5E8" };
const AREA_FG = { spa: "#0F6E56", gym: "#3C3489", pool: "#0077b3", salon: "#A33C6B", activity: "#8A5A0B" };
const BILL_FG = { folio: "#0F6E56", paid: "#27500A", membership: "#3C3489" };
const ST_FG = { booked: "#0C447C", done: "#1D7A44", cancelled: "#A32D2D", no_show: "#8A5A0B" };

export default function ServiceBookingsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [notes, setNotes] = useState({});
  const [sum, setSum] = useState(null);
  const [reschedId, setReschedId] = useState(null);   // booking whose reschedule form is open
  const [reschedVal, setReschedVal] = useState("");   // datetime-local value
  const [payFor, setPayFor] = useState(null);         // booking whose pay-method picker is open
  const [method, setMethod] = useState("cash");       // chosen payment method
  const canSee = ["manager", "controller"].includes(getRole());

  const load = () => fetchServiceBookings().then((d) => {
    setRows(d);
    const n = {}; d.forEach((b) => { n[b.id] = b.resource_note || ""; });
    setNotes(n);
  }).catch(() => setRows([]));
  useEffect(() => {
    load();
    if (canSee) fetchServicesSummary().then(setSum).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const KPI = ({ label, value }) => (
    <div style={{ ...CARD, padding: "10px 14px", minWidth: 130, flex: 1 }}>
      <div className="muted" style={{ fontSize: 11.5 }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 18, color: "#063b48" }}>{value}</div>
    </div>
  );

  const saveNote = (b) => updateServiceBooking(b.id, { resource_note: notes[b.id] || "" }).then((u) => {
    setRows((rs) => (rs || []).map((x) => (x.id === u.id ? u : x)));
  }).catch(() => {});
  const setStatus = (b, status) => updateServiceBooking(b.id, { status }).then((u) => {
    setRows((rs) => (rs || []).map((x) => (x.id === u.id ? u : x)));
  }).catch(() => {});
  // Marking a booking "Done" must settle it, not just flip the status. An unpaid
  // walk-in (pay-direct, no payment yet) opens the payment-method picker and is
  // billed through complete_booking; folio/membership/already-paid just close.
  const markDone = (b) => {
    if (b.billing_kind === "paid" && !b.payment_method) {
      setReschedId(null); setPayFor(b.id); setMethod("cash");
      return;
    }
    setStatus(b, "done");
  };
  const closePay = () => { setPayFor(null); setMethod("cash"); };
  const confirmPay = (b) => completeBooking(b.id, { payment_method: method })
    .then((u) => { setRows((rs) => (rs || []).map((x) => (x.id === u.id ? u : x))); closePay(); })
    .catch(() => window.alert(t("err")));
  // N6 — explicit cancel (with confirm) + reschedule (inline datetime picker;
  // surface the backend 409 conflict message when the new time clashes).
  const cancelBooking = (b) => {
    if (!window.confirm(t("cancel_q"))) return;
    setStatus(b, "cancelled");
  };
  // No-show: mark no-show and (if the guest had paid / a fee applies) charge the
  // booking price. A no-show is final — it can't be rescheduled.
  const markNoShow = (b) => {
    const charge = (b.price || 0) > 0 ? window.confirm(t("noshow_charge_q").replace("{amt}", formatPrice(b.price, cur))) : false;
    noShowBooking(b.id, { charge })
      .then((u) => setRows((rs) => (rs || []).map((x) => (x.id === u.id ? u : x))))
      .catch(() => window.alert(t("err")));
  };
  // Convert an ISO instant to the local "YYYY-MM-DDTHH:MM" value a
  // <input type="datetime-local"> expects (native calendar/clock picker).
  const toLocalInput = (iso) => {
    const d = iso ? new Date(iso) : new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const openReschedule = (b) => { setReschedId(b.id); setReschedVal(toLocalInput(b.scheduled_start)); };
  const closeReschedule = () => { setReschedId(null); setReschedVal(""); };
  const confirmReschedule = (b) => {
    if (!reschedVal) return;
    const when = new Date(reschedVal);
    if (isNaN(when.getTime())) { window.alert(t("err")); return; }
    updateServiceBooking(b.id, { scheduled_start: when.toISOString() })
      .then((u) => { setRows((rs) => (rs || []).map((x) => (x.id === u.id ? u : x))); closeReschedule(); })
      .catch((e) => window.alert((e && e.response && e.response.status === 409)
        ? ((e.response.data && e.response.data.detail && e.response.data.detail.message) || t("conflict"))
        : t("err")));
  };

  // Reprint a receipt for a paid walk-in / member booking. Browser print
  // (window.open) so it works without any thermal hardware — mirrors the
  // F&B / reservation receipt styling.
  const printBooking = async (b) => {
    let d;
    try { d = await fetchServiceBookingReceipt(b.id); } catch { return; }
    const m = (v) => formatPrice(v || 0, cur);
    const hasTax = (d.vat || 0) > 0;
    const svcLine = (d.service_charge || 0) > 0
      ? `<tr><td>${t("r_service")}</td><td class="r">${m(d.service_charge)}</td></tr>` : "";
    const vatLine = hasTax
      ? `<tr><td>${t("r_vat")}${d.vat_pct ? ` ${d.vat_pct}%` : ""}</td><td class="r">${m(d.vat)}</td></tr>` : "";
    const netLine = (svcLine || vatLine)
      ? `<tr><td>${t("r_subtotal")}</td><td class="r">${m(d.subtotal)}</td></tr>` : "";
    const html = `<html><head><meta charset="utf-8"><title>${d.hotel || ""} — ${t("receipt")}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;max-width:360px;margin:0 auto;padding:16px;color:#111}
      h2{margin:0;font-size:17px}.muted{color:#666;font-size:11.5px}
      .head{text-align:center}.title{text-align:center;font-weight:700;font-size:14px;letter-spacing:.5px;margin:8px 0 2px}
      table{width:100%;border-collapse:collapse;margin-top:6px;font-size:12.5px}td{padding:3px 0;vertical-align:top}
      .r{text-align:right}.tot td{border-top:1px solid #999;font-weight:700;padding-top:6px}
      .hr{border-top:1px dashed #999;margin:8px 0}</style></head><body>
      <div class="head"><h2>${d.hotel || ""}</h2></div>
      <div class="title">${t("receipt")}</div>
      <div class="muted head">${d.date ? new Date(d.date).toLocaleString() : ""}</div>
      <div class="hr"></div>
      <div class="muted"><b>${d.service_name || ""}</b>${d.area ? ` · ${t(d.area)}` : ""}</div>
      <div class="muted">${d.customer_name || ""}</div>
      ${(Array.isArray(d.selected_items) && d.selected_items.length) ? `<div class="muted">${t("options")}: ${d.selected_items.join(", ")}</div>` : ""}
      ${d.special_request ? `<div class="muted">${t("special_request")}: ${d.special_request}</div>` : ""}
      <table>${netLine}${svcLine}${vatLine}
        <tr class="tot"><td>${t("r_total")}</td><td class="r">${m(d.total)}</td></tr></table>
      ${d.method ? `<div class="muted" style="margin-top:8px">${d.method}</div>` : ""}
    </body></html>`;
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) return;
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 250);
  };

  const shown = (rows || []).filter((b) => filter === "all" || b.area === filter);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {canSee && sum && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
          <KPI label={t("k_revenue")} value={formatPrice(sum.services_revenue, cur)} />
          <KPI label={t("k_dues")} value={formatPrice(sum.membership_dues, cur)} />
          <KPI label={t("k_members")} value={sum.active_members} />
          <KPI label={t("k_bookings")} value={sum.bookings_total} />
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 16 }}>
        {["all", ...AREAS].map((a) => (
          <button key={a} onClick={() => setFilter(a)}
            className={filter === a ? "btn primary small" : "btn secondary small"}>{t(a)}</button>
        ))}
      </div>

      {rows == null ? <p className="muted" style={{ marginTop: 16 }}>…</p> : shown.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 12, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {shown.map((b) => (
            <div key={b.id} style={{ ...CARD, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ background: AREA_TINT[b.area] || "#eef2f2", color: AREA_FG[b.area] || "#5b6472", borderRadius: 16, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>{t(b.area || "all")}</span>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontWeight: 700 }}>{b.service_name}</div>
                <div className="muted" style={{ fontSize: 12.5 }}>{b.customer_name || "—"}{b.customer_phone ? ` · ${b.customer_phone}` : ""}{b.created_at ? ` · ${new Date(b.created_at).toLocaleString()}` : ""}</div>
                {(b.staff_resource_name || b.room_resource_name) && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    {b.staff_resource_name ? `${t("staff")}: ${b.staff_resource_name}` : ""}
                    {b.staff_resource_name && b.room_resource_name ? " · " : ""}
                    {b.room_resource_name ? `${t("room")}: ${b.room_resource_name}` : ""}
                  </div>
                )}
                {Array.isArray(b.selected_items) && b.selected_items.length > 0 && (
                  <div className="muted" style={{ fontSize: 12 }}>{t("options")}: {b.selected_items.join(", ")}</div>
                )}
                {b.special_request && (
                  <div className="muted" style={{ fontSize: 12 }}>{t("special_request")}: {b.special_request}</div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input style={{ ...inp, width: 140 }} placeholder={t("resource")} value={notes[b.id] || ""}
                  onChange={(e) => setNotes({ ...notes, [b.id]: e.target.value })}
                  onBlur={() => saveNote(b)} />
              </div>
              {(() => {
                // Show the real payment state: a walk-in booked to pay direct is
                // only "Paid" once a payment was actually recorded (payment_method).
                const pendingPay = b.billing_kind === "paid" && !b.payment_method;
                const label = pendingPay ? t("b_paid_pending") : t("b_" + b.billing_kind);
                const color = pendingPay ? "#8A5A0B" : (BILL_FG[b.billing_kind] || "#5b6472");
                return <span style={{ color, fontSize: 12, fontWeight: 700 }}>{label}</span>;
              })()}
              <span style={{ fontWeight: 700, color: "#27500A", minWidth: 70, textAlign: "right" }}>{formatPrice(b.price, cur)}</span>
              <select style={{ ...inp, color: ST_FG[b.status] || "#24313A", fontWeight: 600 }} value={b.status} onChange={(e) => (e.target.value === "no_show" ? markNoShow(b) : e.target.value === "done" ? markDone(b) : setStatus(b, e.target.value))}>
                <option value="booked">{t("st_booked")}</option>
                <option value="done">{t("st_done")}</option>
                <option value="cancelled">{t("st_cancelled")}</option>
                <option value="no_show">{t("st_no_show")}</option>
              </select>
              {((b.billing_kind === "paid" && b.payment_method) || b.billing_kind === "membership") && (
                <button className="btn secondary small" onClick={() => printBooking(b)} title={t("print")}>🧾 {t("print")}</button>
              )}
              {b.status !== "cancelled" && b.status !== "done" && (
                reschedId === b.id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", flexBasis: "100%", marginTop: 6 }}>
                    {/* Split date + time: native pickers close on selection and commit immediately, unlike datetime-local. */}
                    <input style={inp} type="date" value={reschedVal ? reschedVal.slice(0, 10) : ""} aria-label={t("new_time")}
                      onChange={(e) => setReschedVal(e.target.value ? `${e.target.value}T${reschedVal ? reschedVal.slice(11, 16) : "09:00"}` : "")} />
                    <input style={inp} type="time" value={reschedVal ? reschedVal.slice(11, 16) : ""} aria-label={t("new_time")}
                      onChange={(e) => setReschedVal(`${reschedVal ? reschedVal.slice(0, 10) : toLocalInput().slice(0, 10)}T${e.target.value || "09:00"}`)} />
                    <button className="btn primary small" onClick={() => confirmReschedule(b)}>{t("confirm")}</button>
                    <button className="btn secondary small" onClick={closeReschedule}>{t("cancel")}</button>
                  </div>
                ) : (
                  <>
                    <button className="btn secondary small" onClick={() => openReschedule(b)}>{t("reschedule")}</button>
                    <button onClick={() => cancelBooking(b)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>{t("cancel")}</button>
                  </>
                )
              )}
              {payFor === b.id && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", flexBasis: "100%", marginTop: 6 }}>
                  <span className="muted" style={{ fontSize: 12 }}>{t("method")}:</span>
                  <select style={inp} value={method} onChange={(e) => setMethod(e.target.value)}>
                    <option value="cash">{t("m_cash")}</option>
                    <option value="telebirr">{t("m_telebirr")}</option>
                    <option value="cbe_birr">{t("m_cbe")}</option>
                    <option value="pos">{t("m_pos")}</option>
                  </select>
                  <button className="btn primary small" onClick={() => confirmPay(b)}>{t("collect")} · {formatPrice(b.price, cur)}</button>
                  <button className="btn secondary small" onClick={closePay}>{t("cancel")}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
